import PropTypes from 'prop-types';
import { useForm, yupResolver } from '@mantine/form';
import * as yup from 'yup';
import { Button, DatePicker, SelectInput, Textarea, TimePicker } from 'ui';

import { MEDIUM_OPTIONS, NO_MEDIUM } from './contact-activity-utils';

/**
 * An activity records something that already happened, so a future date and time
 * is rejected. The server applies the same rule with a small tolerance for clock
 * skew; this keeps the user from getting there in the first place.
 */
const validation = yup.object({
  occurredAt: yup
    .date()
    .required('A date and time is required.')
    .test('not-future', 'That is in the future — pick when this actually happened.', (value) =>
      value ? value.getTime() <= Date.now() : false,
    ),
  medium: yup.string(),
  notes: yup.string().max(2000, 'Notes must be 2000 characters or fewer.'),
});

/**
 * Inline composer for logging outreach against a contact. Expands in place at
 * the top of the timeline rather than opening over it, so the existing history
 * stays readable while you type. Doubles as the edit form for an existing entry.
 *
 * The caller remounts this via a `key` when switching between adding and editing
 * (see ContactActivity), so `initialValues` is the only seeding path. Re-seeding
 * an already-mounted form from an effect would race the user's own edits — the
 * effect flushes after mount and would overwrite whatever they had just typed.
 */
const ActivityComposer = (props) => {
  const { entry, saving, onSave, onCancel } = props;

  const form = useForm({
    initialValues: {
      occurredAt: entry ? new Date(entry.timestamp) : new Date(),
      medium: entry?.medium ?? NO_MEDIUM,
      notes: entry?.notes ?? '',
    },
    validate: yupResolver(validation),
    validateInputOnBlur: true,
  });

  const handleSubmit = (values) =>
    onSave({
      occurredAt: values.occurredAt.toISOString(),
      medium: values.medium === NO_MEDIUM ? null : values.medium,
      notes: values.notes?.trim() ? values.notes.trim() : null,
    });

  // DatePicker and TimePicker each edit half of the same Date, matching the
  // convention in SuggestEmailModal.
  const setOccurredAt = (date) => form.setFieldValue('occurredAt', date);

  return (
    <form
      noValidate
      onSubmit={form.onSubmit(handleSubmit)}
      className="space-y-3 rounded-lg border border-gray-150 p-3"
      data-testid="activity-composer"
    >
      <div className="flex gap-3">
        <SelectInput
          id="activity-medium"
          label="Type"
          options={MEDIUM_OPTIONS}
          disabled={saving}
          containerClassName="flex-1 min-w-0"
          // Spread before the error props: mantine hands back `error` as a
          // message string, while these inputs want a boolean plus helperText.
          {...form.getInputProps('medium')}
          error={!!form.errors.medium}
          helperText={form.errors.medium}
          size="lg"
        />

        <DatePicker
          id="activity-date"
          label="Date"
          value={form.values.occurredAt}
          onChange={setOccurredAt}
          // An activity cannot have happened after today.
          toDate={new Date()}
          disabled={saving}
          required
          error={!!form.errors.occurredAt}
          containerClassName="flex-1 min-w-0"
        />

        <TimePicker
          label="Time"
          value={form.values.occurredAt}
          onChange={setOccurredAt}
          // Without this, today's options start at the current time and a call
          // from this morning could not be logged.
          allowPastTimes
          disabled={saving}
          required
          containerClassName="w-[150px] shrink-0"
        />
      </div>

      {!!form.errors.occurredAt && <p className="text-sm text-red-500">{form.errors.occurredAt}</p>}

      <Textarea
        id="activity-notes"
        label="Notes"
        placeholder="Add notes (optional)…"
        rows={3}
        disabled={saving}
        containerClassName="w-full"
        {...form.getInputProps('notes')}
        error={!!form.errors.notes}
        helperText={form.errors.notes}
        size="lg"
      />

      <div className="flex justify-end gap-2">
        <Button title="Cancel" size="sm" onClick={onCancel} disabled={saving} />
        <Button
          title={entry ? 'Save changes' : 'Save'}
          size="sm"
          color="primary"
          type="submit"
          loading={saving}
          disabled={saving}
        />
      </div>
    </form>
  );
};

ActivityComposer.propTypes = {
  /** The entry being edited, or undefined when logging a new one. */
  entry: PropTypes.object,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ActivityComposer;
